import { useState, useEffect } from 'react'
import { AlertTriangle } from 'lucide-react'
import { Modal } from '../ui/Modal'
import { Spinner } from '../ui/Spinner'
import { useCurrency } from '../../hooks/useCurrency'
import { usePermissions } from '../../hooks/usePermissions'
import { useIdempotencyKey } from '../../hooks/useIdempotencyKey'
import { supabase } from '../../lib/supabase'
import toast from 'react-hot-toast'

export type CancelPurchasePO = {
  id: string
  order_number: string
  total: number
  amount_paid: number | null
  payment_status: string
  status: string
  delivery_status: string | null
  supplierName?: string
}

type CancelResult = {
  order_number?: string
  method?: string
  total?: number
  advance_released?: number
  advance_cascade_applied?: { applied?: number } | null
}

// Unified cancel modal for purchase orders. Auto-detects the accounting
// treatment from the PO's payment state and routes to the right RPC:
//   • in-transit POs → cancel_goods_in_transit_atomic
//   • received POs   → cancel_purchase_atomic
// The user only ever picks an optional write-off override + a reason.
export function CancelPurchaseModal({
  open, onClose, po, tenantId, userId, onSuccess,
}: {
  open: boolean
  onClose: () => void
  po: CancelPurchasePO | null
  tenantId: string
  userId: string | undefined
  onSuccess: () => void
}) {
  const { format } = useCurrency()
  const { can } = usePermissions()
  const canCancel = can('purchases.return')
  const [reason, setReason] = useState('')
  const [writeOff, setWriteOff] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const idem = useIdempotencyKey()

  // Fresh idempotency key each time the modal opens against a new PO.
  useEffect(() => {
    if (open) idem.reset()
  }, [open, po?.id, idem])

  if (!po) return null

  const supplierName = po.supplierName?.trim() || 'this supplier'
  const isInTransit = po.delivery_status === 'in_transit' || po.status === 'in_transit'
  const paid = Math.max(0, po.amount_paid ?? 0)
  const unpaid = Math.max(0, po.total - paid)
  const isPaid = paid > 0
  const reasonOk = reason.trim().length >= 5
  const goodsWord = isInTransit ? 'goods-in-transit' : 'stock'

  async function handleConfirm() {
    if (!po || !reasonOk || submitting) return
    if (!canCancel) { toast.error('Not authorized to cancel purchase'); return }
    setSubmitting(true)
    try {
      const rpcName = isInTransit ? 'cancel_goods_in_transit_atomic' : 'cancel_purchase_atomic'
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const rpcParams: Record<string, any> = {
        p_tenant_id: tenantId,
        p_purchase_order_id: po.id,
        p_reason: reason.trim(),
        p_cancel_method: writeOff ? 'write_off' : 'auto',
        p_created_by: userId ?? null,
      }
      // Only cancel_purchase_atomic (Batch 2, mig 248) accepts an idempotency
      // key; cancel_goods_in_transit_atomic does not, so never pass it there.
      if (!isInTransit) rpcParams.p_idempotency_key = idem.get()
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any).rpc(rpcName, rpcParams)
      if (error) throw new Error(error.message)

      const r = (data ?? {}) as CancelResult
      const cascade = Number(r.advance_cascade_applied?.applied ?? 0)
      let msg = `${po.order_number} cancelled.`
      if (r.method === 'write_off') {
        msg = `${po.order_number} cancelled. ${format(paid)} written off as a loss.`
      } else if (r.method === 'net_off') {
        msg = `${po.order_number} cancelled. ${format(paid)} now sits as advance with ${supplierName}.`
        if (cascade > 0) msg += ` ${format(cascade)} auto-applied to other open POs.`
      } else {
        msg = `${po.order_number} cancelled. ${format(po.total)} cleared from ${supplierName}'s payable.`
      }
      toast.success(msg)
      idem.reset()
      setReason('')
      setWriteOff(false)
      onSuccess()
      onClose()
    } catch (err) {
      toast.error('Cancel failed: ' + (err instanceof Error ? err.message : String(err)))
    } finally {
      setSubmitting(false)
    }
  }

  function handleClose() {
    if (submitting) return
    setReason('')
    setWriteOff(false)
    onClose()
  }

  return (
    <Modal open={open} onClose={handleClose} title={`Cancel ${po.order_number}?`}>
      <div className="space-y-4">
        {/* Summary */}
        <div className="bg-gray-50 rounded-xl p-4 grid grid-cols-3 gap-3">
          <div>
            <p className="text-xs text-gray-500 mb-0.5">Supplier</p>
            <p className="text-sm font-medium text-gray-800 truncate">{po.supplierName?.trim() || 'Supplier'}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-0.5">Total</p>
            <p className="text-sm font-bold text-red-600">{format(po.total)}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-0.5">Paid</p>
            <p className="text-sm font-semibold text-gray-900">{paid > 0 ? format(paid) : '—'}</p>
          </div>
        </div>

        {/* What will happen */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-800 space-y-1">
          {!writeOff && !isPaid && (
            <>
              <p>This purchase has no payments. Cancelling will:</p>
              <ul className="list-disc list-inside space-y-0.5 text-amber-700">
                <li>Remove the {goodsWord} ({format(po.total)} worth)</li>
                <li>Clear {format(po.total)} from Accounts Payable</li>
                <li>Reverse the original journal entry</li>
              </ul>
            </>
          )}
          {!writeOff && isPaid && (
            <>
              <p>{format(paid)} was already paid on this purchase. Cancelling will:</p>
              <ul className="list-disc list-inside space-y-0.5 text-amber-700">
                <li>Remove the {goodsWord} from your books</li>
                <li>Convert {format(paid)} into a supplier advance with {supplierName}, applied to future purchases</li>
                {unpaid > 0 && <li>Clear the remaining {format(unpaid)} from Accounts Payable</li>}
                <li>Reverse the journals</li>
              </ul>
              <p className="pt-1 text-amber-600">
                This does not refund cash to your bank. If {supplierName} sends the money back, record it as a separate supplier receipt.
              </p>
            </>
          )}
          {writeOff && (
            <>
              <p>The {format(paid)} paid will be recorded as a loss. Cancelling will:</p>
              <ul className="list-disc list-inside space-y-0.5 text-amber-700">
                <li>Remove the {goodsWord} from your books</li>
                <li>Post {format(paid)} to Stock Write-Off (expense)</li>
                {unpaid > 0 && <li>Clear the remaining {format(unpaid)} from Accounts Payable</li>}
              </ul>
            </>
          )}
        </div>

        {/* Write-off override (only relevant when something was paid) */}
        {isPaid && (
          <label className="flex items-start gap-2.5 cursor-pointer">
            <input
              type="checkbox"
              checked={writeOff}
              onChange={e => setWriteOff(e.target.checked)}
              className="mt-0.5 accent-red-500"
            />
            <span className="text-xs text-gray-600">
              <span className="font-medium text-gray-800">Write off as a loss instead.</span>{' '}
              Choose this only if you will never recover the {format(paid)} paid (e.g. the supplier
              disappeared). The paid amount hits the Stock Write-Off expense.
            </span>
          </label>
        )}

        {/* Reason */}
        <div>
          <label className="text-xs font-medium text-gray-700 block mb-1">
            Reason for cancellation <span className="text-red-500">*</span>
          </label>
          <textarea
            value={reason}
            onChange={e => setReason(e.target.value)}
            rows={3}
            placeholder="e.g. Supplier could not fulfil; order placed in error"
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:border-red-400 focus:outline-none resize-none"
          />
          <p className="text-xs text-gray-400 mt-1">Minimum 5 characters. Stored on the PO for audit.</p>
        </div>

        <div className="flex gap-2">
          <button
            onClick={handleClose}
            disabled={submitting}
            className="flex-1 border border-gray-200 rounded-xl py-2.5 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50">
            Keep PO
          </button>
          {canCancel && (
            <button
              onClick={handleConfirm}
              disabled={submitting || !reasonOk}
              className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-xl py-2.5 text-sm font-bold disabled:opacity-50 flex items-center justify-center gap-2">
              {submitting ? <Spinner size="sm" /> : (<><AlertTriangle className="h-4 w-4" /> Confirm Cancel</>)}
            </button>
          )}
        </div>
      </div>
    </Modal>
  )
}
